// NPM Imports
import React, { PureComponent, Fragment } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import uuid from "react-native-uuid";
import { Button, Tooltip } from "@revvsales/components";

// Components Imports
import {
  DocApproval,
  DocRename,
  DocStatusUpdate,
  EsignWarning,
  MoveDocStageToDraft,
  SignerDetails,
  UnFilledFormFields,
  EsignWarningOnApprovedStage,
  DocEsignFillerStatusUpdate,
} from "../../Popups";
import { reactionsOnESignBtnClick } from "../../Parser";
import ShowEsignLimitIncreaseRequestPopup from "../../../ShowEsignLimitIncreaseRequestPopup/index";

// Internal Imports
import { AmplitudeUtils } from "../../../../utils";
import { EVENTS } from "../../../../constants";

// Selector Imports
import {
  isSignersListValid,
  signersListCount,
  fetchSignersList,
} from "./eSignBtnSelector";

// Destructuring
const { amplitudeLogEvent } = AmplitudeUtils;

// Declarations
const payload = {
  esignUsageLimit: { limit: 0, usage: 0 },
  lastExecutedMethodIndex: null,
  skipDocNameCheck: false,
  skipDocStatusUpdate: false,
  skipFormFieldsFilledCheck: false,
  workFlowEventID: uuid.v4(),
};

class eSignBtn extends PureComponent {
  state = {
    showApprovalModal: false,
    showDocRenameModal: false,
    showDocStatusUpdateModal: false,
    showEsignLimitExceedModal: false,
    showEsignWarning: false,
    showEsignWarningOnApprovedStagePopup: false,
    showMoveDocStageToDraftModal: false,
    showSignerDetailsModal: false,
    showUnFilledFormFieldsWarningModal: false,
    showEsignFillerDocStatusUpdateModal: false,
  };

  _onEsignBtnClick = () => {
    // Reset the skip logic when e-sign btn is clicked
    Object.assign(payload, {
      skipDocNameCheck: false,
      skipDocStatusUpdate: false,
      skipFormFieldsFilledCheck: false,
      lastExecutedMethodIndex: null,
    });
    this._triggerESignBtnActions({});
  };

  _triggerESignBtnActions = async ({ stateChange }) => {
    const {
      documentID,
      documentName,
      documentStage,
      formFields,
      isFormMode,
      isSignersCheckListValid,
      signersCheckListCount,
    } = this.props;

    amplitudeLogEvent(EVENTS.EVENT_NAMES.ESIGN_BUTTON_CLICKED, {
      SignerCount: signersCheckListCount,
    });

    Object.assign(payload, {
      documentID,
      documentName,
      documentStage,
      formFields,
      isFormMode,
      isSignersCheckListValid,
    });
    const viewToShow = await reactionsOnESignBtnClick({
      payload,
    });
    this.setState({ ...stateChange, [viewToShow]: true });
  };

  _closeModalOrTriggerNxtView = ({ triggerNxt, ...otherStates }) => {
    if (triggerNxt) {
      this._triggerESignBtnActions({ stateChange: { ...otherStates } });
    } else {
      this.setState({ ...otherStates });
    }
  };

  render() {
    const {
      disabled,
      disabledMsg,
      documentID,
      documentName,
      signersAccessList,
    } = this.props;
    const {
      showApprovalModal,
      showDocRenameModal,
      showDocStatusUpdateModal,
      showEsignLimitExceedModal,
      showEsignWarning,
      showEsignWarningOnApprovedStagePopup,
      showMoveDocStageToDraftModal,
      showSignerDetailsModal,
      showUnFilledFormFieldsWarningModal,
      showEsignFillerDocStatusUpdateModal,
    } = this.state;
    return (
      <Fragment>
        {/* ----- COMPONENT SECTION STARTS ----- */}
        {disabled && (
          <Tooltip
            placement="bottom"
            align={{
              offset: [0, 4],
            }}
            overlay={disabledMsg}>
            <Button
              className="esignbtn disabled"
              onClick=""
              data-test="e-sign-btn">
              <FontAwesomeIcon icon={["far", "signature"]} />
              &nbsp; Get eSign
            </Button>
          </Tooltip>
        )}

        {!disabled && (
          <Tooltip
            placement="bottom"
            align={{
              offset: [0, 4],
            }}
            overlay="Send document for eSignature">
            <Button
              className="esignbtn"
              onClick={this._onEsignBtnClick}
              data-test="e-sign-btn">
              <FontAwesomeIcon icon={["far", "signature"]} />
              &nbsp; Get eSign
            </Button>
          </Tooltip>
        )}
        {/* ----- COMPONENT SECTION ENDS ----- */}

        {/* ----- POPUP SECTION STARTS ----- */}
        {/* DOCUMENT RENAME POPUP */}
        {showDocRenameModal && (
          <DocRename
            documentName={documentName}
            documentID={documentID}
            onClose={prop => {
              this._closeModalOrTriggerNxtView({
                showDocRenameModal: false,
                ...prop,
              });
            }}
          />
        )}

        {/* SHOW WARNING IF NOT ALL FORM FIELDS ARE FILLED */}
        {showUnFilledFormFieldsWarningModal && (
          <UnFilledFormFields
            formFields={this.props.formFields}
            onClose={prop => {
              this._closeModalOrTriggerNxtView({
                showUnFilledFormFieldsWarningModal: false,
                ...prop,
              });
            }}
          />
        )}

        {/* NO E-SIGN BLOCK / E-SIGN DATA MISSING POPUP */}
        {showEsignWarning && (
          <EsignWarning
            onClose={prop => {
              this._closeModalOrTriggerNxtView({
                showEsignWarning: false,
                ...prop,
              });
            }}
          />
        )}

        {/* No E-SIGN BLOCK IN APPROVED STAGE */}
        {showEsignWarningOnApprovedStagePopup && (
          <EsignWarningOnApprovedStage
            showEsignWarningOnApprovedStagePopup={
              showEsignWarningOnApprovedStagePopup
            }
            closePopup={prop => {
              this._closeModalOrTriggerNxtView({
                showEsignWarningOnApprovedStagePopup: false,
                ...prop,
              });
            }}
          />
        )}

        {/* APROVAL FLOW POPUP */}
        {showApprovalModal && (
          <DocApproval
            workFlowEventID={payload.workFlowEventID}
            onClose={prop => {
              this._closeModalOrTriggerNxtView({
                showApprovalModal: false,
                ...prop,
              });
            }}
          />
        )}

        {/* SIGNER DETAILS POPUP */}
        {showSignerDetailsModal && (
          <SignerDetails
            signersAccessList={signersAccessList}
            onClose={prop => {
              this._closeModalOrTriggerNxtView({
                ...prop,
              });
            }}
          />
        )}

        {/* MOVE DOCUMENT TO DRAFT STAGE CONFIRM POPUP */}
        {showMoveDocStageToDraftModal && (
          <MoveDocStageToDraft
            showDialogBox={false}
            onClose={prop => {
              this._closeModalOrTriggerNxtView({
                showMoveDocStageToDraftModal: false,
                ...prop,
              });
            }}
          />
        )}

        {/* DOCUMENT STAGE UPDATE POPUP */}
        {showDocStatusUpdateModal && (
          <DocStatusUpdate
            onClose={prop => {
              this._closeModalOrTriggerNxtView({
                showDocStatusUpdateModal: false,
                ...prop,
              });
            }}
          />
        )}
        {showEsignFillerDocStatusUpdateModal && (
          <DocEsignFillerStatusUpdate
            onClose={prop => {
              this._closeModalOrTriggerNxtView({
                showEsignFillerDocStatusUpdateModal: false,
                ...prop,
              });
            }}
          />
        )}

        {/* E-SIGN LIMIT EXCEEDED POPUP */}
        {showEsignLimitExceedModal && (
          <ShowEsignLimitIncreaseRequestPopup
            eSignCosumptionRecord={payload.esignUsageLimit}
            onClose={prop => {
              this._closeModalOrTriggerNxtView({
                showEsignLimitExceedModal: false,
                ...prop,
              });
            }}
          />
        )}
        {/* ----- POPUP SECTION ENDS ----- */}
      </Fragment>
    );
  }
}

eSignBtn.defaultProps = {
  disabled: false,
  disabledMsg: "",
};
eSignBtn.propTypes = {
  disabled: PropTypes.bool,
  disabledMsg: PropTypes.string,
};

const mapStateToProps = state => {
  const {
    document: {
      fields_used: formFields = [],
      id: documentID,
      name: documentName,
      showForm: isFormMode = false,
      signatories,
      stage: documentStage,
      docNo: documentNo,
    },
    layout,
  } = state.DocumentStore;

  // Local Assignments
  const isSignersCheckListValid = isSignersListValid(signatories);
  const signersCheckListCount = signersListCount(signatories);
  const signersAccessList = fetchSignersList(signatories, layout);

  return {
    documentID,
    documentName,
    documentStage,
    formFields,
    isFormMode,
    isSignersCheckListValid,
    signersCheckListCount,
    signersAccessList,
    documentNo,
  };
};

export default connect(mapStateToProps)(eSignBtn);



// WEBPACK FOOTER //
// ./src/components/QuotationTopBarV3/Buttons/eSignBtn/index.js